/**
 * Version history for a recipe (L4 — Google Docs style).
 *
 * Sheet with two views:
 *   · List: local versions (PowerSync bucket, last ~10 offline) +
 *     deep history on demand via paginated API.
 *   · Detail: word-by-word diff against CURRENT recipe text
 *     (name + ingredients + steps) + full text of version +
 *     "Restore this version" (normal forward write: current state
 *     is archived as new version — history is never rewritten).
 */
import { createEffect, createMemo, createSignal, For, on, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { ArrowLeft, History, Loader2, RotateCcw } from "@fleet/icons";
import { cn } from "@mealplan/shared";
import type { RecipeIngredient, RecipeVersion } from "@mealplan/powersync";
import type { components } from "@mealplan/client";
import { useRecipeVersionsQuery } from "@mealplan/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { api } from "@/lib/api";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@fleet/ui";
import { WordDiff } from "@/components/ui/word-diff";
import { useI18n } from "@/providers/i18n-provider";
import { formatRelativeTime } from "@/features/activity/lib/activity-text";

const PREVIEW_MAX_CHARS = 120;
const LOCAL_SYNC_WINDOW = 10;
const DEEP_PAGE_SIZE = 20;

interface RecipeVersionHistorySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipeId: () => string;
  /** Current name (diff reference). */
  currentName: () => string;
  /** Current ingredients of the recipe (diff reference). */
  currentIngredients: () => RecipeIngredient[];
  /** Current steps of the recipe (diff reference). */
  currentSteps: () => string[];
  /** Restore: caller performs the write and closes if appropriate. */
  onRestore: (version: RecipeVersion) => void;
  restoring?: () => boolean;
}

/** API row (camelCase, generated types) → local shape (snake_case). */
type ApiVersion = components["schemas"]["RecipeVersion"];

function fromApi(row: ApiVersion, userId: string | null): RecipeVersion {
  return {
    id: row.id,
    user_id: userId,
    recipe_id: row.recipeId,
    name: row.name,
    servings: row.servings,
    steps: row.steps,
    tags: row.tags,
    notes: row.notes ?? null,
    photo_url: row.photoUrl ?? null,
    ingredients: JSON.stringify(row.ingredients),
    text: row.text,
    client_ts: row.clientTs ?? null,
    server_ts: row.serverTs,
  };
}

/** Formats quantity without trailing zeros (mirrors server: 1.0 → "1"). */
function formatQuantity(quantity: number | null): string {
  if (quantity === null || Number.isNaN(quantity)) {
    return "";
  }
  return String(quantity);
}

/** Plain text of a recipe (name + "quantity unit ingredient" + steps),
 * mirror of `text` composed by server for word diff. */
function buildRecipeText(name: string, ingredients: RecipeIngredient[], steps: string[]): string {
  const parts: string[] = [name];
  const ordered = ingredients.toSorted((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  for (const ingredient of ordered) {
    const line = [formatQuantity(ingredient.quantity), ingredient.unit ?? "", ingredient.name ?? ""]
      .filter((part) => part.length > 0)
      .join(" ")
      .trim();
    parts.push(line);
  }
  for (const step of steps) {
    parts.push(step);
  }
  return parts.join("\n");
}

function preview(text: string | null): string {
  const clean = (text ?? "").replaceAll(/\s+/gu, " ").trim();
  return clean.length > PREVIEW_MAX_CHARS ? `${clean.slice(0, PREVIEW_MAX_CHARS)}…` : clean;
}

export function RecipeVersionHistorySheet(props: RecipeVersionHistorySheetProps) {
  const { t, locale } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();

  const versions = useRecipeVersionsQuery({
    db,
    drizzle,
    userId: session.userId,
    recipeId: () => props.recipeId(),
  });

  /** Current plain text of the recipe (for the diff). */
  const currentText = createMemo(() =>
    buildRecipeText(props.currentName(), props.currentIngredients(), props.currentSteps()),
  );

  // Deep history (API): accumulated pages + cursor.
  const [deepVersions, setDeepVersions] = createSignal<RecipeVersion[]>([]);
  const [deepCursor, setDeepCursor] = createSignal<string | null>(null);
  const [deepExhausted, setDeepExhausted] = createSignal(false);
  const [deepLoading, setDeepLoading] = createSignal(false);
  const [selected, setSelected] = createSignal<RecipeVersion | null>(null);
  const [view, setView] = createSignal<"changes" | "content">("changes");

  const allVersions = createMemo<RecipeVersion[]>(() => {
    const local = versions.data ?? [];
    const seen = new Set(local.map((v) => v.id));
    return [...local, ...deepVersions().filter((v) => !seen.has(v.id))];
  });

  // Only offer "load more" if local window is full (if fewer
  // than 10 local, ALL history is already on the device).
  const mayHaveDeeper = createMemo(
    () => (versions.data ?? []).length >= LOCAL_SYNC_WINDOW && !deepExhausted(),
  );

  const loadOlder = async () => {
    if (deepLoading()) {
      return;
    }
    setDeepLoading(true);
    try {
      const cursor = deepCursor() ?? allVersions().at(-1)?.id ?? undefined;
      const { data, error } = await api.GET("/api/recipes/recipe/{id}/versions", {
        params: { path: { id: props.recipeId() }, query: { cursor, limit: DEEP_PAGE_SIZE } },
      });
      if (error || !data) {
        return;
      }
      const uid = session.userId();
      setDeepVersions((prev) => [...prev, ...data.versions.map((v) => fromApi(v, uid))]);
      setDeepCursor(data.nextCursor ?? null);
      if (!data.nextCursor) {
        setDeepExhausted(true);
      }
    } catch (error) {
      console.error("Error loading older recipe versions:", error);
    } finally {
      setDeepLoading(false);
    }
  };

  // On close (by the user OR programmatically after restore), the next
  // open always starts on the LIST, never on a stale detail.
  createEffect(
    on(
      () => props.open,
      (open) => {
        if (!open) {
          setSelected(null);
          setView("changes");
        }
      },
      { defer: true },
    ),
  );

  const close = (open: boolean) => {
    props.onOpenChange(open);
  };

  return (
    <Dialog open={props.open} onOpenChange={close} contentClass="max-w-lg">
      <div
        class="flex max-h-[70vh] min-h-[40vh] flex-col gap-3 p-1"
        data-testid="recipe-version-history"
      >
        <Show
          when={selected()}
          fallback={
            <>
              <div class="flex items-center gap-2">
                <History class="h-4 w-4 text-accent" />
                <DialogTitle>{t(msg`Version history`)}</DialogTitle>
              </div>
              <p class="text-xs text-muted-foreground">
                {t(msg`Earlier versions of this recipe are kept here as you edit it.`)}
              </p>

              <div class="flex flex-col gap-2 overflow-y-auto" data-testid="recipe-version-list">
                <Show
                  when={!versions.isLoading}
                  fallback={
                    <div class="flex flex-col gap-2">
                      <Skeleton class="h-16 w-full rounded-xl" />
                      <Skeleton class="h-16 w-full rounded-xl" />
                      <Skeleton class="h-16 w-full rounded-xl" />
                    </div>
                  }
                >
                  <Show
                    when={allVersions().length > 0}
                    fallback={
                      <EmptyState
                        icon={History}
                        title={t(msg`No versions yet`)}
                        description={t(
                          msg`Keep editing — earlier versions of this recipe will be kept here.`,
                        )}
                      />
                    }
                  >
                    <For each={allVersions()}>
                      {(version, index) => (
                        <button
                          type="button"
                          data-testid={`recipe-version-${index()}`}
                          class="flex flex-col gap-1 rounded-xl border border-border/50 bg-muted/20 p-3 text-left transition-colors hover:border-accent/30 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                          onClick={() => setSelected(version)}
                        >
                          <span class="text-xs font-semibold text-foreground">
                            {formatRelativeTime(version.client_ts ?? version.server_ts, locale())}
                          </span>
                          <Show when={version.name}>
                            <span class="truncate text-sm font-semibold text-foreground/90">
                              {version.name}
                            </span>
                          </Show>
                          <span class="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                            {preview(version.text)}
                          </span>
                        </button>
                      )}
                    </For>
                    <Show when={mayHaveDeeper()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="text-muted-foreground"
                        data-testid="recipe-version-load-more"
                        disabled={deepLoading()}
                        onClick={() => {
                          void loadOlder();
                        }}
                      >
                        <Show when={deepLoading()}>
                          <Loader2 class="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        </Show>
                        {t(msg`Load older versions`)}
                      </Button>
                    </Show>
                  </Show>
                </Show>
              </div>
            </>
          }
        >
          {(version) => (
            <>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label={t(msg`Back to version list`)}
                  data-testid="recipe-version-back"
                  onClick={() => setSelected(null)}
                >
                  <ArrowLeft class="h-4 w-4" />
                </button>
                <DialogTitle class="flex-1 truncate">
                  {formatRelativeTime(version().client_ts ?? version().server_ts, locale())}
                </DialogTitle>
                <div class="flex items-center rounded-lg bg-muted/40 p-0.5 text-xs">
                  <button
                    type="button"
                    data-testid="recipe-version-view-changes"
                    class={cn(
                      "rounded-md px-2 py-1 text-muted-foreground",
                      view() === "changes" && "bg-background font-medium text-foreground shadow-sm",
                    )}
                    onClick={() => setView("changes")}
                  >
                    {t(msg`Changes`)}
                  </button>
                  <button
                    type="button"
                    data-testid="recipe-version-view-content"
                    class={cn(
                      "rounded-md px-2 py-1 text-muted-foreground",
                      view() === "content" && "bg-background font-medium text-foreground shadow-sm",
                    )}
                    onClick={() => setView("content")}
                  >
                    {t(msg`Full recipe`)}
                  </button>
                </div>
              </div>

              <div class="flex-1 overflow-y-auto rounded-xl border border-border/40 bg-muted/10 p-3">
                <Show
                  when={view() === "changes"}
                  fallback={
                    <p
                      class="whitespace-pre-wrap break-words text-sm leading-[1.75] text-foreground"
                      data-testid="recipe-version-content"
                    >
                      {version().text}
                    </p>
                  }
                >
                  <WordDiff oldText={version().text ?? ""} newText={currentText()} />
                </Show>
              </div>

              <p class="text-[11px] leading-relaxed text-muted-foreground/70">
                {t(
                  msg`Restoring never deletes history: what you have now is saved as a new version first.`,
                )}
              </p>
              <div class="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    close(false);
                  }}
                >
                  {t(msg`Cancel`)}
                </Button>
                <Button
                  size="sm"
                  data-testid="restore-recipe-version"
                  disabled={props.restoring?.() === true}
                  onClick={() => {
                    props.onRestore(version());
                  }}
                >
                  <Show
                    when={props.restoring?.() === true}
                    fallback={<RotateCcw class="mr-1.5 h-3.5 w-3.5" />}
                  >
                    <Loader2 class="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  </Show>
                  {t(msg`Restore this version`)}
                </Button>
              </div>
            </>
          )}
        </Show>
      </div>
    </Dialog>
  );
}
